import { useState } from "react";
import { useNavigate } from "@entry/navigation";

import { CodeInput } from "@components/Input";
import { Loader } from "@components/Loader";
import { OnboardingLayoutProps } from "@components/OnboardingLayout";
import { Spacing, SpacingSizes } from "@components/Spacing";
import { Body } from "@components/Typography";
import { Routes } from "@ctypes";
import { EntryStepLayout as OnboardingLayout } from "@entry/EntryStepLayout";
import {
  useErrorBanner,
  useFetchVerifiedPrefill,
  useOnboarding,
  usePrefill,
  useUser,
  useVerifyPhoneNumber,
} from "@hooks";
import { Translations, i18n } from "@i18n";
import { formatMaskedSsnNumber, formatPhoneNumber } from "@utils";
import { styles } from "./styles";

const CODE_LENGTH = 6;

export const PhoneVerificationRoute = () => {
  const navigate = useNavigate();

  const { showErrorBanner } = useErrorBanner();

  const { user, loading: userLoading } = useUser();

  const [loading, setLoading] = useState(false);
  const [code, setCode] = useState("");

  const [verifyPhoneNumber] = useVerifyPhoneNumber();
  const [fetchVerifiedPrefill] = useFetchVerifiedPrefill();

  const { setPrefillProps } = usePrefill();

  const { resetToNext } = useOnboarding();

  const disabled = code.length !== CODE_LENGTH;

  const startVerification = async (code: string) => {
    try {
      setLoading(true);

      const { data } = await verifyPhoneNumber({
        variables: {
          code,
        },
      });

      if (data?.verifyPhoneNumber) {
        const { data: prefill } = await fetchVerifiedPrefill();

        const prefillData = prefill?.userPrefillData;

        const hasPrefillData = Boolean(
          prefillData?.firstName &&
          prefillData.lastName &&
          prefillData.dateOfBirth &&
          prefillData.address?.street &&
          prefillData.address.city &&
          prefillData.address.postalCode &&
          prefillData.address.state &&
          prefillData.ssn &&
          user?.email &&
          user.phoneNumber,
        );

        if (hasPrefillData) {
          const prefillData = prefill?.userPrefillData;

          setPrefillProps({
            firstName: prefillData?.firstName,
            lastName: prefillData?.lastName,
            address: prefillData?.address?.street,
            address2: prefillData?.address?.unitNo,
            city: prefillData?.address?.city,
            state: prefillData?.address?.state,
            zipCode: prefillData?.address?.postalCode,
            ssn: formatMaskedSsnNumber(prefillData?.ssn ?? ""),
          });

          navigate(Routes.ReviewProfile, { replace: true });
        } else {
          resetToNext();
        }
      }
    } catch {
      showErrorBanner();
    } finally {
      setLoading(false);
    }
  };

  const props: OnboardingLayoutProps = {
    title: i18n.t(Translations.PhoneVerificationRouteTitle),
    cta: {
      primary: {
        title: i18n.t(Translations.PhoneVerificationRoutePrimaryCTA),
        onClick: () => startVerification(code),
        loading,
        disabled,
      },
    },
  };

  if (userLoading) return <Loader />;

  const number = formatPhoneNumber(user?.phoneNumber ?? "");

  return (
    <OnboardingLayout {...props}>
      <CodeInput
        length={CODE_LENGTH}
        value={code}
        onChangeText={setCode}
        onComplete={(code: string) => startVerification(code)}
      />

      <Body style={styles.subtitle}>
        {i18n.t(Translations.PhoneVerificationRouteSubtitle, { number })}
      </Body>
      <Spacing size={SpacingSizes.M} />
    </OnboardingLayout>
  );
};
